import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/core/auth/AuthContext';
import { resourcesService } from '../services/resources.service';
import type { ResourceCreatePayload, ResourceUpdatePayload } from '../types/resources.types';
import { toast } from 'sonner';

export function useResources() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const userId = profile?.id || '';
  const queryClient = useQueryClient();

  const queryKey = ['inventory', 'estimate-resources'];

  const query = useQuery({
    queryKey,
    queryFn: () => resourcesService.fetchResources(),
  });

  const createMutation = useMutation({
    mutationFn: (payload: ResourceCreatePayload) => resourcesService.createResource(userId, companyId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Thêm tài nguyên thành công');
    },
    onError: (err: any) => {
      console.error(err);
      toast.error(err.message || 'Lỗi khi thêm tài nguyên');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ResourceUpdatePayload }) => 
      resourcesService.updateResource(userId, companyId, id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Cập nhật tài nguyên thành công');
    },
    onError: (err: any) => {
      console.error(err);
      toast.error(err.message || 'Lỗi khi cập nhật tài nguyên');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => resourcesService.deleteResource(userId, companyId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Xóa tài nguyên thành công');
    },
    onError: (err: any) => {
      console.error(err);
      toast.error(err.message || 'Lỗi khi xóa tài nguyên');
    },
  });

  return {
    data: query.data || [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    createResource: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    updateResource: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    deleteResource: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
}
